import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { salesOrderMaster } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import ConfirmDeleteModal from "../../common/DeleteModel";
import SalesOrderForm from "./SalesOrderForm";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";

const SalesOrderMaster = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    order_No: 0,
    cust_Id: "",
    order_Date: "",
    delivery_Days: "",
    order_Status_Id: "",
    total_Weight: "",
    total_Qty: "",
    aolly_Id: "",
    calculate_dispatch_date: "",
    packing_Instructions: "",
    general_Notes: "",
    nalco_Rate: "",
    nalco_Addition: "",
    final_Rate: "",
    created_By: userId,
    OrderDetails: [
      {
        dieId: "",
        powder_Coating_Color: "",
        Profile_Length: "",
        qty: "",
        qty_Weight: "",
        created_By: userId,
        unit_Conv_Id: "",
      },
    ],
  });
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);

  const fetchSaleOrderData = async () => {
    setLoading(true);

    try {
      const res = await salesOrderMaster.getAllOrders(currentPage, pageSize);
      if (currentPage === 1) {
        setOrderData(res.data || []);
      } else {
        setOrderData((prev) => [...prev, ...(res.data || [])]);
      }

      if (!res.data || res.data.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching die master data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 120) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchSaleOrderData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setOrderData(orderData);
    } else {
      const filtered = orderData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setOrderData(filtered);
    }
  }, [searchQuery, orderData]);

  const handleAdd = () => {
    setIsEditing(false);
    setSelectedOrderData(null);
    setIsModelOpen(true);
  };

  const handleEdit = async (id) => {
    try {
      const response = await salesOrderMaster.getById(id);
      if (response.data) {
        const fetchedData = response.data;
        const formattedInitialData = {
          ...fetchedData,
          order_No: fetchedData.order_No,
          order_Date: fetchedData.order_Date?.split("T")[0],
          calculate_dispatch_date:
            fetchedData.calculate_dispatch_date?.split("T")[0],
          delivery_Days: fetchedData.delivery_Days?.toString() || "",
          OrderDetails: fetchedData.orderDetails.map((item) => ({
            order_Detail_Id: item.order_Detail_Id,
            dieId: item.id,
            profileLengthFt: item.profile_Length,
            quantityPcs: item.qty,
            qtyWeight: item.qty_Weight,
            coatingColor: item.powder_Coating_Color,
            unit_Conv_Id: item.unit_Conv_Id,
            currentWeight: item.currentWeight,
          })),
        };

        setSelectedOrderData(formattedInitialData);
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch sales order master details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await salesOrderMaster.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchSaleOrderData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSave = async (salesOrderData, id) => {
    setIsSaving(true);
    try {
      await salesOrderMaster.saveOrUpdate(salesOrderData);
      notify(id ? "Updated successfully" : "Added successfully", "success");
      fetchSaleOrderData();
      setIsModelOpen(false);
      setFormData("");
      setIsEditing(false);
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const totalQty = orderData.reduce(
    (sum, x) => sum + Number(x.total_Qty || 0),
    0,
  );
  const totalWeight = orderData.reduce(
    (sum, x) => sum + Number(x.totalEst || 0),
    0,
  );

  return (
    <>
      <div>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col">
            <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
              Sale Order
            </h2>
            <p className="text-sm text-gray-500">
              Manage and track customer sales orders
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-72 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LiaSearchSolid className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search by Order No..."
                className="block w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <Button
              text="Add Order"
              className="bg-orange text-black hover:bg-white font-medium px-4 py-2 rounded-lg flex items-center shadow-sm transition-all"
              icon={HiOutlinePlus}
              onClick={handleAdd}
            />
          </div>
        </div>
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          {loading && currentPage === 1 ? (
            <Loader />
          ) : (
            <div className="min-w-max">
              <Table
                columns={[
                  { key: "order_No", label: "Or No." },
                  { key: "customerName", label: "Customer" },
                  { key: "aolly_Name", label: "Alloy" },
                  { key: "itemCount", label: "Items" },
                  { key: "total_Qty", label: "Total Qty (Pcs)" },
                  { key: "totalEst", label: "Total Est. Wt (kg)" },
                  { key: "status_Name", label: "Status" },
                  {
                    key: "order_Date",
                    label: "Order Date",
                    render: (value) => formatDate(value),
                  },
                  {
                    key: "calculate_dispatch_date",
                    label: "Dispatch Date",
                    render: (value) => formatDate(value),
                  },
                  { key: "delivery_Days", label: "Lead Time (Days)" },
                ]}
                data={orderData}
                onEdit={(row) => handleEdit(row.order_No)}
                onDelete={(row) => handleDelete(row.order_No)}
              />
            </div>
          )}
        </div>
        <div className="flex justify-center mt-6">
          <div className="bg-[#1e293b] text-white px-8 py-3 rounded-2xl shadow-2xl flex items-center gap-12 border border-slate-700">
            <div className="flex flex-col items-center">
              <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                Total Pieces
              </span>
              <span className="text-xl font-black">
                {totalQty.toLocaleString()}
              </span>
            </div>

            <div className="h-8 w-[1px] bg-slate-600"></div>

            <div className="flex flex-col items-center">
              <span className="text-[10px] uppercase tracking-[2px] text-slate-400 font-bold">
                Total Weight
              </span>
              <span className="text-xl font-black">
                {totalWeight.toFixed(3)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {isModelOpen && (
        <SalesOrderForm
          initialData={selectedOrderData}
          onSave={handleSave}
          onCancel={() => setIsModelOpen(false)}
          isEditing={isEditing}
          isSaving={isSaving}
        />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default SalesOrderMaster;
